import React from 'react';
import Promise from 'bluebird';
import { actions } from 'xstate';
import useMachine from '../../hooks/useMachine';
import { useQueryParams, StringParam, NumberParam } from 'use-query-params';
import get from 'lodash/get';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import DashboardContext from '../DashboardContext';
import { useApolloClient } from 'react-apollo-hooks';
import gql from 'graphql-tag';
import { AmplifyModelContext } from 'amplify-model-react';
const { assign } = actions;

export const LOADING_FLAG = 'loading';

export const MyTeamContext = React.createContext();
const machineConfig = {
  id: 'myTeamContext',
  context: {},
  initial: 'initializing',
  states: {
    initializing: {
      on: {
        DONE: {
          actions: 'evtValsToCtx',
          target: 'showList'
        },
        CHANGE_PERIOD: {
          actions: 'evtValsToCtx',
          target: 'showList'
        }
      }
    },
    showList: {
      onEntry: 'initTable',
      on: {
        CHANGE_FILTER: {
          actions: 'showList'
        },
        CHANGE_VIEW_MODE: {
          actions: 'evtValsToCtx'
        },
        CHANGE_PERIOD: {
          actions: 'evtValsToCtx'
        },
        VIEW_MEMBER: {
          actions: 'viewMember'
        },
        OPEN_MEMBER_DIALOG: {
          actions: 'evtValsToCtx',
          target: 'viewMemberDialog'
        }
        // INACTIVATE: {
        //   target: 'showScreenSaver',
        // },
        // LOG_OUT: {
        //   target: 'loggedOut',
        // },
      }
    },
    viewMemberDialog: {
      on: {
        CHANGE_TAB: {
          target: 'viewMemberDialog',
          actions: 'evtValsToCtx'
        },
        CLOSE: {
          target: 'showList',
          actions: 'evtValsToCtx'
        },
        CHANGE_PERIOD: {
          actions: 'evtValsToCtx'
        }
      }
    },
    showScreenSaver: {
      on: {
        ACTIVATE: {
          target: 'showContent'
        }
      }
    },
    loggedOut: {
      type: 'final'
    }
  },
  onDone: {
    actions: 'onAuthentication'
  }
};

const machineOptions = {
  actions: {
    changePage: assign(() => {
      return {};
    }),
    evtValsToCtx: assign((ctx, evt) => {
      return { ...evt.value };
    })
  },
  guards: {},
  services: {}
};

export function isMemberActive(memberOrResult) {
  let result;
  if (!memberOrResult) return false;
  if (memberOrResult.actualWagePercent) result = memberOrResult;
  else {
    result = get(memberOrResult, 'planResults.items.0');
  }
  return get(result, 'actualWagePercent') > 0;
}

async function fetchPpr({ apollo, basicFieldsString, memberPprs, currentPeriod }) {
  const newMemberPprs = {};
  let shouldFetch = false,
    q = '';
  Object.entries(memberPprs).forEach((entry, ii) => {
    const [memberId, val] = entry;
    newMemberPprs[memberId] = val;
    if (val === LOADING_FLAG) {
      newMemberPprs[memberId] = null;
      shouldFetch = true;
      q += `
    qPpr_${ii} : searchPeriodPlanResults(
      filter: {
        periodPlanResultMemberId: { eq: "${memberId}" }
        periodPlanResultPeriodId: { eq: "${currentPeriod.id}" }
      }
      sort: { field: createdAt, direction: desc }
      limit: 1
    ) {
      items {
        ${basicFieldsString}
        managerFeedback {
          commenter 
          body 
          date
        }
      }
    }
    `;
    }
  });
  if (shouldFetch) {
    await Promise.delay(1500);
    // ^^ adds 2 seconds buffer time to let the update propagate to server
    // eventual consistent for dynamo and elasticsearch
    const result = await apollo.query({
      query: gql`
  query {
    ${q}
  }
  `,
      fetchPolicy: 'network-only'
    });

    console.log('>>MyTeamContext/index::', 'result', result); //TRACE
    Object.values(get(result, 'data', {})).forEach(val => {
      const ppr = get(val, 'items.0');
      const pprMemberId = get(ppr, 'periodPlanResultMemberId');
      if (pprMemberId) {
        newMemberPprs[pprMemberId] = ppr;
      }
    });
    console.log('>>MyTeamContext/index::', 'newMemberPprs', newMemberPprs); //TRACE
    return newMemberPprs;
  }
}

export function Provider(props) {
  const [dialogQuery, setDialogQuery] = useQueryParams({
    tabIndex: NumberParam,
    memberId: StringParam,
    refPlanResultId: StringParam,
    refQueryKey: StringParam,
    focusField: StringParam
  });
  const [state, setState] = React.useState({
    memberPprs: {},
    memberList: [],
    filters: {}
  });
  const { memberPprs } = state;
  const { state: dashboardState } = React.useContext(DashboardContext);
  const currentPeriod = get(dashboardState, 'context.currentPeriod', {});
  const initialDialogState = pickBy(dialogQuery, identity);
  const initialContext = {};
  if (Object.keys(initialDialogState).length > 0) {
    initialContext.dialog = initialDialogState;
  }
  const machine = useMachine({
    machineConfig,
    machineOptions,
    initialContext
  });
  const dialogState = get(machine, 'state.context.dialog');
  React.useEffect(() => {
    const { tabIndex, memberId, refPlanResultId, refQueryKey, focusField } =
      dialogState || {};
    setDialogQuery({ tabIndex, memberId, refPlanResultId, refQueryKey, focusField });
  }, [dialogState, setDialogQuery]);

  const apollo = useApolloClient();
  const { getModelSchema } = React.useContext(AmplifyModelContext);
  const { basicFieldsString } = React.useMemo(() => getModelSchema('PeriodPlanResult'), [
    getModelSchema
  ]);

  React.useEffect(() => {
    if (!currentPeriod) return;
    let unmounted = false;
    fetchPpr({ apollo, basicFieldsString, memberPprs, currentPeriod }).then(
      newMemberPprs => {
        if (!newMemberPprs || unmounted) return;
        console.log('>>MyTeamContext/index::', 'updating...'); //TRACE
        setState(oldState => ({ ...oldState, memberPprs: newMemberPprs }));
      }
    );
    return () => {
      unmounted = true;
    };
  }, [apollo, currentPeriod, basicFieldsString, memberPprs]);

  const setMembers = React.useCallback(members => {
    console.log('>>MyTeamContext/index::', 'setMembers,', members); //TRACE
    setState(oldState => ({
      ...oldState,
      memberList: members,
      memberPprs: Object.fromEntries(members.map(m => [m.id, LOADING_FLAG]))
    }));
  }, []);

  const contextState = React.useMemo(
    () => ({
      ...state,
      ...machine,
      setFilters(filters) {
        console.log('>>MyTeamContext/index::', 'filters 11', filters); //TRACE
        setState(oldState => ({
          ...oldState,
          filters: { ...(oldState.filters || {}), ...filters }
        }));
      },
      setMembers,
      refetchMemberPPr(memberId) {
        if (!memberId) return;
        console.log('>>MyTeamContext/index::', 'refetching member ppr for ' + memberId); //TRACE
        setState(oldState => {
          return {
            ...oldState,
            memberPprs: { ...oldState.memberPprs, [memberId]: LOADING_FLAG }
          };
        });
      }
    }),
    [machine, state, setMembers]
  );
  return (
    <MyTeamContext.Provider value={contextState}>{props.children}</MyTeamContext.Provider>
  );
}

export default MyTeamContext;
